import React, { useCallback } from 'react';
import styled from 'styled-components';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';
import { CustomTooltip } from './CustomTooltip';

const LegendText = styled.span`
  color: var(--brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
`;

// Memoized formatter for the legend
const formatLegend = (value) => <LegendText>{value}</LegendText>;

function DeviceUsagePieChart({ data }) {
  // Color scheme for different device types
  const colors = {
    mobile: '#3B82F6', // Blue
    tablet: '#10B981', // Green
    desktop: '#F59E0B' // Orange
  };

  // Show empty state if no data
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <EmptyState>
        <EmptyMessage>No device usage data available</EmptyMessage>
      </EmptyState>
    );
  }

  // Data is already in the correct format from Analytics Factory
  const chartData = data.map((device) => ({
    name: device.name,
    value: device.count,
    fill: colors[device.name.toLowerCase()] || '#8884d8'
  }));

  // Memoize the tooltip content to prevent unnecessary re-renders
  const renderTooltip = useCallback(
    (props) => <CustomTooltip {...props} chartData={chartData} />,
    [chartData]
  );

  return (
    <Container>
      <ResponsiveContainer width="100%" height={320}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={120}
            paddingAngle={2}
            dataKey="value">
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Pie>
          <Tooltip content={renderTooltip} />
          <Legend verticalAlign="bottom" height={45} formatter={formatLegend} />
        </PieChart>
      </ResponsiveContainer>
    </Container>
  );
}

const Container = styled.div`
  align-items: center;
  display: flex;
  height: 350px;
  justify-content: center;
  width: 100%;
`;

const EmptyState = styled.div`
  align-items: center;
  background: #f9f9f9;
  border-radius: 8px;
  display: flex;
  height: 300px;
  justify-content: center;
`;

const EmptyMessage = styled.p`
  color: var(--light-brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  margin: 0;
`;

export default DeviceUsagePieChart;
